/**
 * Shared image utilities for Contentful Image API URL construction.
 *
 * Hero components (SimpleHero, HeroLead, SubjectSpotlight) use
 * Contentful-direct <picture> with avif/webp sources. Pass `format` so
 * Contentful returns the optimised format directly — no /_next/image proxy.
 * renderPage preloads use the same URLs for byte-identical preload matching.
 *
 * Card components use cardSrc() — same Contentful Image API sizing without
 * format negotiation (background-images can't use <picture>).
 */

export const IMAGE_QUALITY_MOBILE = 80
// Was 98 — visually indistinguishable from 82 in avif/webp but produces
// meaningfully larger files. See docs/LIGHTHOUSE_RESPONSE.md #2.
export const IMAGE_QUALITY_DESKTOP = 82

/**
 * Hero <picture> tablet band (640–1023px). These sources go direct to
 * Contentful, so this value does not need to appear in the next.config.ts
 * `qualities` array — that only constrains /_next/image.
 */
export const IMAGE_QUALITY_TABLET = 85

export const HERO_SOURCES = {
  mobile: { w: 640, h: 720 },
  tablet: { w: 1024, h: 576 },
  desktop: { w: 1920, h: 810 },
  desktop2x: { w: 2880, h: 1620 },
} as const

export const CARD_SOURCES = {
  bound: { w: 413, h: 200 },
  unbound: { w: 300, h: 200 },
  bound2x: { w: 826, h: 400 },
} as const

export function cardSrc(
  base: string,
  width: number,
  height: number,
  focus?: string | null,
  quality: number = IMAGE_QUALITY_MOBILE,
): string {
  const url = base.startsWith("http") ? base : `https:${base}`
  const f = focus ? `&f=${focus}` : ""
  return `${url}?w=${width}&h=${height}&fit=fill&q=${quality}${f}`
}

export function heroSrc(
  base: string,
  width: number,
  height: number,
  focus: string,
  format?: "avif" | "webp",
  quality?: number,
): string {
  const url = base.startsWith("http") ? base : `https:${base}`
  const fm = format ? `fm=${format}&` : ""
  const q = quality !== undefined ? `&q=${quality}` : `&q=${IMAGE_QUALITY_DESKTOP}`
  return `${url}?${fm}w=${width}&h=${height}&fit=fill&f=${focus}${q}`
}
